import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  ActiveTestQueuedStateService,
  AGENT_TYPE,
  ApiService,
  Device,
  DevicesService,
  DialogService,
  EntityCacheProvider,
  FormatService,
  IDeviceData,
  ISite,
  LmapService,
  NotificationService,
  TestAssembleService,
  TestServerConfigService,
  TransformationService,
  ValuesExchangeProvider,
  TabulatorService,
  RunTypeValue
} from '@nrwl/common-lib';
import { WarningMessage } from '@nrwl/monitor-management';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';
import { get } from 'lodash';
import { Subject, Observable } from 'rxjs';
import { ScheduledIntervals } from './models/types';

interface IBulkError {
  name: string;
  issue: string;
}

@Injectable({
  providedIn: 'root'
})
export class MultiStepSetupService extends BaseSetupService implements OnDestroy {
  state: ActivatedRoute;
  entityCache: EntityCacheProvider;
  transformService: TransformationService;
  formatService: FormatService;
  lmapService: LmapService;
  testAssembleService: TestAssembleService;
  serverConfigService: TestServerConfigService;
  activeTestQueuedStateService: ActiveTestQueuedStateService;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tabDataChangeEvents = new Subject<any>();
  testTaskName = 'multistep';
  deviceModel = 'vTA';
  selectedOriginator: Device;
  selectedDestination: Device;
  selectedTestType = {
    name: 'Web Transactions',
    description: 'Monitor web performance metrics of a defined application',
    id: 'multistep'
  };
  monitorId = '';
  tenantId = 'paas.local';
  siteList = [];
  selectedDevices = [];
  selectedSites = [];
  TEST_LIMIT_COUNT = 2000;
  private monitorMaxLimitUrl = '/monitor/MonitorManager/v1/monitor/activeMonitorsCountAndLimit';
  private montiorUrl = '/monitor/MonitorManager/v1/monitor';
  private scriptURL = '/entity/v1/application';
  private readonly unsubscribe: Subject<void> = new Subject();
  runType = RunTypeValue.ON_GOING;
  testConfig: any;

  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    dialogService: DialogService,
    dialog: MatDialog,
    entityCache: EntityCacheProvider,
    transformService: TransformationService,
    lmapService: LmapService,
    formatService: FormatService,
    testAssmebleService: TestAssembleService,
    serverConfigService: TestServerConfigService,
    activeTestQueuedStateService: ActiveTestQueuedStateService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly notifyService: NotificationService,
    private readonly deviceService: DevicesService,
    private tabulatorService: TabulatorService
  ) {
    super(apiService, dialogService, dialog);
    this.state = state;
    this.tabClass = BaseSetupTab;
    this.entityCache = entityCache;
    this.transformService = transformService;
    this.formatService = formatService;
    this.lmapService = lmapService;
    this.testAssembleService = testAssmebleService;
    this.serverConfigService = serverConfigService;
    this.activeTestQueuedStateService = activeTestQueuedStateService;
  }

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    agentBasedConfig: any
  ): void {
    super.openTestSetup(
      setupId,
      setupConfig,
      defaultParamsById,
      onSuccess,
      onFail,
      agentBasedConfig
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
    this.tabDataChangeEvents.next(index);
  }

  setOriginatorAgent(device: Device): void {
    this.selectedOriginator = device;
  }

  setDestinationAgent(device: Device): void {
    this.selectedDestination = device;
  }

  //eslint-disable-next-line
  getApplicationScripts$(id: number): Observable<any> {
    return this.apiService.get$(`${this.scriptURL}/${id}/script/all/`, {});
  }

  getUpdatedDeviceData(data: Device[]): Device[] {
    for (let i = 0; i < data.length; i++) {
      const platform = data[i].deviceData.params.find(x => x.name === 'platform')
        ? data[i].deviceData.params.find(x => x.name === 'platform').value
        : '';
      data[i].deviceData.iconName = this.transformService.getIconDetail(
        data[i].deviceData.status,
        platform
      );
      data[i].deviceData['hidden'] = false;
    }
    return data;
  }

  roundToOneDecimal(data: number): number {
    return data ? +data?.toFixed(1) : data;
  }

  removeWindowAgentDevice(deviceData: Array<Device>): Array<Device> {
    const deviceTyoe = [AGENT_TYPE.WINDOWS];
    return deviceData.filter(data => {
      return !this.checkForSupportiveAgent(data, deviceTyoe);
    });
  }
  checkForSupportiveAgent(data: Device, deviceType: Array<AGENT_TYPE>): boolean {
    const platform = this.getParamsValueByName(data.deviceData.params, 'platform');
    const agentType = this.transformService.getAgentTypeBasedOnData(platform);
    return deviceType.includes(agentType);
  }

  // eslint-disable-next-line
  getParamsValueByName(params: any, name: string): any {
    const paramsObj = params.find(x => x.name === name);
    return paramsObj && paramsObj.value ? paramsObj.value : '';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getDestination(device: any, site: ISite): any {
    return {
      alias: this.selectedTestType.name,
      destination: {
        agent: this.getOriginator(device, site)
      },
      tests: this.getTestTypes()
    };
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTestTypes(): any[] {
    return [
      {
        description: this.selectedTestType.description,
        id: this.selectedTestType.id,
        label: this.selectedTestType.name
      }
    ];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getOriginator(device: any, site: any): any {
    return {
      alias: device.deviceData.name,
      id: device.deviceData.id,
      isCloudAgent: true,
      location: {
        coordinate: {
          lat: site.sitesData?.location.lat,
          lon: site.sitesData?.location.lon
        },
        name: site.sitesData.name
      },
      status: device.deviceData.status,
      siteName: site.sitesData.name,
      country: site?.sitesData?.location?.country || ''
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getMonitorMaxLimit$(): any {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.get$<any>(this.monitorMaxLimitUrl, {});
  }
  //eslint-disable-next-line
  saveSetupData(): any {
    const deviceLength = this.output.selectedOriginator.length;
    if (deviceLength > 1) {
      const param = {
        id: 'addservice.createTest',
        alternateTitle:
          'Add ' + this.formatService.formatNumberWithCommas(deviceLength) + ' tests?',
        callBackService: this,
        alternateMsg:
          'This action will create ' +
          this.formatService.formatNumberWithCommas(deviceLength) +
          ' tests and add them to the system? Would you like to continue?',
        callBackFuntion: this.addServiceTest
      };
      this.dialogService.showDialog('confirm', 'body', param);
    } else {
      this.addServiceTest(this);
    }
  }

  getBulkPayload = (): any => {
    let firstConfig: any = null;
    const allAgentsAndTests: any[] = [];
    this.output.selectedOriginator.forEach((device, index) => {
      const payload = this.getCreatePayload(index, device);
      for (let i = 0; i < payload.agents.length; i++) {
        if (
          payload.agents[i].primary.id === device.deviceData.id &&
          payload.tests[i]['test-list-data'].tpInfo[0].id
        ) {
          const payloadCombinedAgent = payload.agents[i];
          const payloadCombinedtest = payload.tests[i];
          const agentAndTest = {
            agent: payloadCombinedAgent,
            test: payloadCombinedtest
          };
          allAgentsAndTests.push(agentAndTest);
        }
      }

      // Store the first config value if not already set
      if (index === 0) {
        firstConfig = payload.config;
      }
    });

    // Create the final structured object outside of the forEach function
    return {
      agents: allAgentsAndTests, // Consolidated array of agents and tests in grouped objects
      config: firstConfig[0] // Only the first config object
    };
  };

  // eslint-disable-next-line
  addServiceTest(service): void {
    const deviceLength = service.output.selectedOriginator.length;
    const finalStructure = service.getBulkPayload();
    if (deviceLength <= 25) {
      service.createTestWithProgressBar(deviceLength, finalStructure); // Use finalStructure instead of data
    } else {
      // More than 50 test creation show toast message instead of progress bar
      service.createTestWithToastMessage(finalStructure); // Use finalStructure instead of data
    }
  }
  //eslint-disable-next-line
  createTestWithProgressBar(deviceLength: number, data: any): void {
    const message = deviceLength === 1 ? 'Creating test...' : 'Creating tests...';
    this.openTestStatusDialog(message);
    const responseProcessed = this._saveRequest(data, false);
    responseProcessed.subscribe(response => {
      this.onMonitorCreationSuccess(response);
      this.exchange.notifyListeners('new_test', response);
    });
  }

  //eslint-disable-next-line
  createTestWithToastMessage(data: any): void {
    const responseProcessed = this._saveRequest(data, false);
    //close immediately for bulk selection test
    this.exchange.notifyListeners('new_test', {});
    this.notifyService.onSuccess(
      'Test creation is in progress and may take some time. We will let you know when completed.'
    );
    responseProcessed.subscribe(response => {
      if (this.requestFailed) {
        response = { [this.output.testName]: 'Error:' };
      }
      const isErrorAvailable = Object.keys(response).some(key => response[key].includes('Error:'));
      if (!isErrorAvailable) {
        this.notifyService.onMonitorBulkCreationSuccess();
      } else {
        this.notifyService.onMonitorBulkCreationFailed(
          response,
          this.onMonitorCreationSuccess.bind(this)
        );
      }
    });
  }

  // eslint-disable-next-line
  saveUpdatedSetupData(defaultParamsdata: any): any {
    const output = {};
    const message = 'Updating test...';
    this.openTestStatusDialog(message, 'Update');
    output['alias'] = this.output.alias;
    output['originator'] = defaultParamsdata.deviceData;
    output['monitorId'] = defaultParamsdata.id;
    output['destination'] = defaultParamsdata.selectedAppData;
    output['metaData'] = this.output.proxyConfig;
    output['runType'] = defaultParamsdata.runOnDemand;
    output['testType'] = 'multistep';
    this.monitorId = defaultParamsdata.id;
    output['testConfig'] = this.createLMAPObject(0);
    output['destination'] = {
      agent: get(this.output.destination, 'agentSelector'),
      phonenumber: get(this.output.destination, 'phonenumber')
    };
    output['destination']['name'] = get(defaultParamsdata.selectedAppData, 'label');
    if (defaultParamsdata['testType'] === 'multistep') {
      output['destination']['url'] = get(defaultParamsdata.selectedAppData, 'url');
      output['destination']['curlWithAuth'] = get(
        defaultParamsdata.selectedAppData,
        'curlWithAuth'
      );
    }
    this.dataUrl = this.montiorUrl;
    const responseProcessed = this._saveRequest(output, false);
    responseProcessed.subscribe(response => {
      this.updateTestStatusDialog(
        this.requestFailed ? 'Failed to update test' : 'Test has been updated',
        'Update'
      );

      this.exchange.notifyListeners('new_test', response);
    });
  }

  //eslint-disable-next-line
  getCreatePayload(index: any, device: any): any {
    const payload = [];
    payload.push(this.createLMAPObject(index));
    return {
      agents: payload.map(item => item.agent),
      tests: payload.map(item => item.test),
      config: payload.map(item => item.config)
    };
  }

  //eslint-disable-next-line
  splitURL(url: string): any {
    return {
      protocol: new URL(url).protocol,
      address: new URL(url).hostname,
      port: new URL(url).port
    };
  }

  //eslint-disable-next-line
  getAllTestParams(): any {
    const splitURL = this.splitURL(this.output.selectedApp.subContent);
    const obj = {
      config: {
        dryrun: this.output.precheck ? true : false,
        'target-ip': {
          'ip-version': 'ipv4',
          'target-address': splitURL.address
        },
        ping: {
          'ping-enable': false,
          'ping-count': 0,
          interval: this.output.interval
        },
        traceroute: {
          'traceroute-enable': this.output.precheck ? false : true,
          'number-of-intervals': this.getNumberOfIntervals(),
          interval: this.output.interval,
          'use-icmpecho': false,
          'use-tcpsyn': true,
          'use-udp': false,
          'tcp-port': splitURL.port || (splitURL.protocol === 'http:' ? 80 : 443)
        },
        pagewalk: {
          'pagewalk-enable': true,
          'number-of-intervals': this.getNumberOfIntervals(),
          interval: this.output.interval
        }
      }
    };
    if (this.output.pageUrl) {
      obj['config']['pagewalk']['page-url'] = this.output.pageUrl;
    }
    if (this.output.scriptUrl) {
      obj['config']['pagewalk']['script-url'] = this.output.scriptUrl;
    }
    return obj;
  }

  getNumberOfIntervals(): number {
    if (this.output.runOnDemand === RunTypeValue.ON_DEMAND) {
      return this.output.precheck ? 1 : (8 * 60) / (this.output.interval / 60);
    }
    return 0;
  }
  //eslint-disable-next-line
  createLMAPObject(index): any {
    this.runType = this.output.runType;
    const testPointA = this.output['selectedOriginator']?.[index]?.deviceData;
    const serverConfig = this.serverConfigService.getCollectorConfig();
    const collectorURL = this.lmapService.buildCollectorPostUrl(serverConfig, this.deviceModel);
    const testSchedule = this.createTestSchedule(testPointA, collectorURL);
    const controllerConfig = this.createControllerConfig(testPointA, testSchedule, index);
    if (!this.output.scheduler?.repeatInterval) {
      this.runType = RunTypeValue.ON_DEMAND;
    }
    return controllerConfig;
  }

  //eslint-disable-next-line
  createTestSchedule(deviceData: IDeviceData, collectorURL: string): any {
    // create the test schedule
    const testScheduleName = this.output.alias;
    const testActionName = testScheduleName + '-action';
    let testEventName = testScheduleName + '-schedule-event';
    if (this.output.interval < ScheduledIntervals.THIRTY_MINUTES) {
      testEventName = 'immediate';
    }

    const optionArray = this.getTestActionOptions(deviceData);
    const tags = this.getTagInfo(deviceData);
    const action = this.lmapService.createAction(
      testActionName,
      this.testTaskName,
      optionArray,
      null,
      tags
    );
    const actionArray = [];
    actionArray.push(action);
    // while report schedule references could be created here they will be added
    //automatically to this schedule via addResultScheduleAndEvent()
    const testSchedule = this.lmapService.createSchedule(
      testScheduleName,
      testEventName,
      actionArray
    );

    // build the LMAP configuration (test schedule + result schedules + all events other than those predefined by MA such as immediate)
    let lmapified = this.lmapService.addSchedule(testSchedule);

    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'immediate',
      'pm',
      collectorURL,
      this.output.interval,
      null
    );
    return lmapified;
  }

  //eslint-disable-next-line
  getTestActionOptions(deviceData: IDeviceData): Array<{ id: string; value: string }> {
    const optionArray = [];

    const allTestParams = {};
    allTestParams['multistep'] = this.getAllTestParams();
    const actionOption = {
      id: this.testTaskName,
      value: JSON.stringify(allTestParams)
    };
    optionArray.push(actionOption);
    optionArray.push({ id: 'role', value: 'primary' });
    optionArray.push({ id: 'web-proxy', value: this.output.proxyConfig?.[0]?.value });
    this.output.proxyConfig;
    return optionArray;
  }

  getTagInfo(deviceData: IDeviceData): Array<string> {
    const tags = [];
    tags.push('service-name:' + this.output.selectedApp.value);
    tags.push('monitor-type:' + this.runType);
    tags.push('tenant-id:' + this.tenantId);
    tags.push('application-id:' + this.output.selectedApp.key);
    if (this.output.precheck) {
      tags.push('precheck:' + this.output.precheck);
    }
    if (this.output.scriptName) {
      tags.push('scriptname:' + this.output.scriptName);
    }
    return tags;
  }

  //eslint-disable-next-line
  createControllerConfig(deviceData: IDeviceData, lmapified, index): any {
    const controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      deviceData,
      this.testTaskName,
      lmapified
    );
    controllerConfig.test['extended-config'] = {};
    controllerConfig.test['extended-config']['testPoints'] = [
      {
        id: this.output.selectedOriginator?.[index]?.getDevice().uniqueId,
        ip: this.output.selectedOriginator?.[index]?.getDevice().mgmtIPaddress,
        model: this.output.selectedOriginator?.[index]?.getModel(),
        name: this.output.selectedOriginator?.[index]?.getDevice().deviceName,
        // port: this.output['engine-a-info']?.port,
        selected: true,
        tag: 'Test Point A',
        testPortName: '',
        testPortPhyId: '',
        type: 'inventory',
        vendor: this.output.selectedOriginator?.[index]?.getVendor().toLowerCase(),
        version: this.output.selectedOriginator?.[index]?.getVersion().value
      },
      {
        id: this.output.selectedApp.key,
        name: this.output.selectedApp.value,
        tag: 'Test Point B',
        type: 'application',
        url: this.output.selectedApp.subContent
      }
    ];
    controllerConfig.test['test-list-data']['tpInfo'].push({
      id: this.output.selectedApp.key,
      name: this.output.selectedApp.value,
      type: 'secondary',
      model: 'application',
      url: this.output.selectedApp.subContent
    });
    controllerConfig.test['test-list-data']['runType'] = this.runType;
    controllerConfig.test['test-list-data']['proxyConfig'] = this.output.proxyConfig;
    controllerConfig.test['test-list-data']['interval'] = this.output.interval;
    controllerConfig.test['test-list-data']['scriptUrl'] = this.output.scriptUrl;
    controllerConfig.test['test-list-data']['scriptName'] = this.output.scriptName;
    return controllerConfig;
  }

  //eslint-disable-next-line
  onMonitorCreationSuccess(response: any): void {
    if (response && response?.details && response?.details?.failed > 0) {
      if (response && Array.isArray(response.error)) {
        this.errorMessage = response.message || 'Some tests failed';
        this.errorDescription = response.message || 'Some tests failed';
      }

      this.requestFailed = true;
    }
    const deviceLength = this.requestFailed ? 1 : this.output.selectedOriginator.length;
    const bulkErrors = this.formatBulkErrors(response);
    this.errorDescription = response.message;
    this.updateBulkTestStatusDialog(
      this.createMessage(deviceLength, bulkErrors, false, this.errorDescription),
      this.createMessage(deviceLength, bulkErrors, true, this.errorDescription),
      bulkErrors.length === 0,
      bulkErrors
    );
    this.clearCache();
  }

  clearCache(): void {
    const clearItems = [
      'tabulator-multi-site-selection-group',
      'tabulator-multi-site-selection-filter',
      'tabulator-multi-site-selection-page',
      'tabulator-multi-site-selection-sort',
      'tabulator-multi-device-selection-group',
      'tabulator-multi-device-selection-filter',
      'tabulator-multi-device-selection-sort',
      'tabulator-multi-device-selection-page'
    ];
    clearItems.forEach(item => localStorage.removeItem(item));
  }
  createMessage(
    totalTests: number,
    bulkErrors: Array<IBulkError>,
    description: boolean,
    errorDescription: string
  ): string {
    if (totalTests === 1) {
      if (description) {
        return bulkErrors.length === 0 ? '' : errorDescription;
      } else {
        return bulkErrors.length === 0 ? 'Test has been created' : 'Failed to create test';
      }
    } else {
      if (bulkErrors.length === 0) {
        if (description) {
          return '';
        }
        return 'Tests have been created';
      } else {
        if (description) {
          return totalTests === bulkErrors.length
            ? 'There were some issues adding these tests:'
            : 'However, there were some issues adding these tests:';
        }
        return totalTests === bulkErrors.length
          ? 'Failed to create tests'
          : 'Some of your tests were created';
      }
    }
  }

  //eslint-disable-next-line
  formatBulkErrors(res: any): Array<IBulkError> {
    const failedTests = [];

    // Check if response contains failed tests in a structured way
    if (res && res?.details && res?.details?.failed_tests) {
      failedTests.push(
        ...res.details.failed_tests.map((test: any) => ({
          name: test.name ? test.name : test.schedule_name,
          issue: test.reason
        }))
      );
    }

    return failedTests;
  }

  // eslint-disable-next-line
  getTestAssignedDatasource(data: any, deviceName: string): any {
    const dataSource = [];
    const testAssignedData = data?.queueStatus;
    for (const key in testAssignedData) {
      if (testAssignedData[key]?.total) {
        dataSource.push({
          type: key,
          used: testAssignedData[key]?.used,
          max: testAssignedData[key]?.total,
          className: this.tabulatorService.getTestTypeImage(key),
          maxCount: testAssignedData['maxCount'],
          overalUsed: testAssignedData['overall_total_used'],
          overalPercentage: Number.isNaN(testAssignedData['overall_percentage'])
            ? -1
            : testAssignedData['overall_percentage'],
          deviceName: deviceName,
          hasOveralPercentage: Number.isInteger(testAssignedData['overall_percentage'])
            ? 'block'
            : 'none',
          showProgressBar: Number.isInteger(testAssignedData['overall_percentage'])
            ? 'none'
            : 'block',
          hideView: true
        });
      }
    }
    return dataSource;
  }

  //eslint-disable-next-line
  getActiveTestQueuedStatus(deviceWithMonitors: any, id: string): any {
    return deviceWithMonitors.find(device => device.deviceData.id === id);
  }
  getWarningMessage(siteId: string, agentId: string, status: string): string {
    const message = this.activeTestQueuedStateService.warningMessage(agentId, 'multistep');
    if (status === 'offline') {
      return 'Test will be queued/failed. Resources are offline';
    } else if (
      message === WarningMessage.RESOURCE_AVAILABLE_WARNING_MESSAGE &&
      status === 'online'
    ) {
      return null;
    } else {
      return message;
    }
  }
  showBulkTestLimitDialog(): void {
    const params = { action: 'bulk.selection' };
    const param = {
      id: params.action + '.activeTest',
      alternateMsg: `You can only create ${this.TEST_LIMIT_COUNT} items at a time. Please reduce your amount and try again.`,
      alternateTitle: 'Test count limit exceeded',
      callBackService: this
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }
  showTestLimitDialog(warningMsg: string): void {
    const params = { action: 'start' };
    const param = {
      id: params.action + '.activeTest',
      alternateMsg: warningMsg,
      alternateTitle: 'Test count limit exceeded',
      callBackService: this
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }

  //eslint-disable-next-line
  _createMonitor(data: any): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.post$<any>(this.montiorUrl, data);
  }

  _getTemplateConfigParameters(): any {
    return {
      proxyConfig: this.output.proxyConfig,
      runType: this.output.runType,
      scriptName: this.output.scriptName ?? null,
      interval: this.output.interval
    };
  }
}
